import { NavLink } from "react-router-dom";
import type { CSSProperties } from "react";

export type PublishingStudioTab =
  | "articles"
  | "templates"
  | "partners"
  | "seo"
  | "charity";

interface Props {
  activeTab: PublishingStudioTab;
  badgeCounts?: {
    articles?: number;
    templates?: number;
    partners?: number;
    seo?: number;
    charity?: number;
  };
}

const TABS: {
  id: PublishingStudioTab;
  to: string;
  label: string;
  countKey?: keyof NonNullable<Props["badgeCounts"]>;
}[] = [
  { id: "articles", to: "/admin/articles", label: "📰 Journal & News Wire", countKey: "articles" },
  { id: "templates", to: "/admin/templates", label: "✉️ Email & SMS Templates", countKey: "templates" },
  { id: "partners", to: "/admin/partners", label: "🤝 Brand Partners & Sponsors", countKey: "partners" },
  { id: "seo", to: "/admin/seo", label: "📈 SEO & Referrals", countKey: "seo" },
  { id: "charity", to: "/admin/charity", label: "🕊️ S3 Giving Ledger", countKey: "charity" },
];

export default function PublishingStudioTabs({ activeTab, badgeCounts }: Props) {
  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.badge}>Publishing &amp; Growth Studio</div>
          <h1 style={styles.title}>Publishing &amp; Brand Growth Studio</h1>
          <p style={styles.subtitle}>
            Manage speakeasy editorial articles, automated member message templates, distillery brand sponsorships, and charitable giving.
          </p>
        </div>
      </div>

      <nav style={styles.tabNav} aria-label="Publishing and Growth Studio Tabs">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const count = tab.countKey ? badgeCounts?.[tab.countKey] : undefined;
          return (
            <NavLink
              key={tab.id}
              to={tab.to}
              style={{
                ...styles.tabLink,
                ...(isActive ? styles.tabLinkActive : {}),
              }}
            >
              <span>{tab.label}</span>
              {typeof count === "number" && (
                <span style={isActive ? styles.countActive : styles.countInactive}>
                  {count}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    marginBottom: 20,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#60A5FA",
    background: "rgba(96, 165, 250, 0.12)",
    padding: "3px 8px",
    borderRadius: 4,
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: 13,
    color: "var(--muted)",
  },
  tabNav: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    paddingBottom: 4,
  },
  tabLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--muted)",
    textDecoration: "none",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--border)",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  tabLinkActive: {
    color: "#0D0B08",
    background: "var(--gold)",
    borderColor: "var(--gold)",
    fontWeight: 700,
  },
  countActive: {
    fontSize: 11,
    background: "rgba(0,0,0,0.18)",
    color: "#0D0B08",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 800,
  },
  countInactive: {
    fontSize: 11,
    background: "rgba(255,255,255,0.06)",
    color: "var(--muted)",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 600,
  },
};
